import { WidthAxis, HeightAxis, DepthAxis } from './Item';

/**
 * EMS(Empty Maximal Spaces) 기반 3D 빈패킹 코어.
 *
 * 아이디어: "빈 최대 공간"들의 목록을 유지하고, 각 상자를 가장 잘 맞는(스너그한) 공간의
 * 뒤·아래·왼쪽 코너에 놓는다. 상자를 놓을 때마다 그 상자와 겹치는 공간들을 최대 6개의
 * 슬래브로 쪼개 목록을 갱신한다. 상자 모서리 피벗만 쓰던 방식보다 후보 위치의 질이 높아
 * 혼합 적재에서 적재율이 크게 오른다. (벤치마크: 혼합 기준 v5 대비 +최대 20%)
 *
 * 배치 규칙(고정): margin(스너그 핏) → 낮게 → 뒤·왼쪽. 중력 낙하는 하지 않는다
 * (공간의 바닥면 y0에 그대로 놓는다 — 낙하를 넣으면 공간 정합성이 깨져 오히려 나빠진다).
 * 완전 공중부양(아래 받침 전무)만 금지한다.
 */

const EPS = 0.5;

function mkSpace(x0, y0, z0, x1, y1, z1) { return { x0, y0, z0, x1, y1, z1 }; }
function sw(s) { return s.x1 - s.x0; }
function sh(s) { return s.y1 - s.y0; }
function sd(s) { return s.z1 - s.z0; }

function overlaps(a, b) {
  return a.x0 < b.x1 - EPS && b.x0 < a.x1 - EPS &&
         a.y0 < b.y1 - EPS && b.y0 < a.y1 - EPS &&
         a.z0 < b.z1 - EPS && b.z0 < a.z1 - EPS;
}
function contains(outer, inner) {
  return outer.x0 <= inner.x0 + EPS && inner.x1 <= outer.x1 + EPS &&
         outer.y0 <= inner.y0 + EPS && inner.y1 <= outer.y1 + EPS &&
         outer.z0 <= inner.z0 + EPS && inner.z1 <= outer.z1 + EPS;
}
function overlap1d(a0, al, b0, bl) {
  const o = Math.min(a0 + al, b0 + bl) - Math.max(a0, b0);
  return o > 0 ? o : 0;
}

// 공간 S 를 상자 B(AABB)로 잘라 최대 6개의 최대 슬래브 공간을 만든다.
function difference(S, B) {
  const out = [];
  if (B.x0 > S.x0 + EPS) out.push(mkSpace(S.x0, S.y0, S.z0, B.x0, S.y1, S.z1));
  if (B.x1 < S.x1 - EPS) out.push(mkSpace(B.x1, S.y0, S.z0, S.x1, S.y1, S.z1));
  if (B.y0 > S.y0 + EPS) out.push(mkSpace(S.x0, S.y0, S.z0, S.x1, B.y0, S.z1));
  if (B.y1 < S.y1 - EPS) out.push(mkSpace(S.x0, B.y1, S.z0, S.x1, S.y1, S.z1));
  if (B.z0 > S.z0 + EPS) out.push(mkSpace(S.x0, S.y0, S.z0, S.x1, S.y1, B.z0));
  if (B.z1 < S.z1 - EPS) out.push(mkSpace(S.x0, S.y0, B.z1, S.x1, S.y1, S.z1));
  return out;
}

// 퇴화/과소 공간과 다른 공간에 포함된 공간을 제거한다.
function prune(list, minDim) {
  const kept = list.filter(s => sw(s) >= minDim && sh(s) >= minDim && sd(s) >= minDim);
  const res = [];
  for (let i = 0; i < kept.length; i++) {
    let contained = false;
    for (let j = 0; j < kept.length; j++) {
      if (i === j) continue;
      // 서로 포함(동일)일 때는 인덱스가 작은 쪽만 남긴다.
      if (contains(kept[j], kept[i]) && !(contains(kept[i], kept[j]) && i < j)) { contained = true; break; }
    }
    if (!contained) res.push(kept[i]);
  }
  return res;
}

// 완전 공중부양 여부: y>0 인데 바로 아래에 받쳐주는 상자가 전혀 없으면 true.
function isFullyFloating(placed, d, x, y, z) {
  if (y <= EPS) return false;
  for (const p of placed) {
    if (Math.abs(p.pos[HeightAxis] + p.dim[HeightAxis] - y) <= EPS) {
      if (overlap1d(x, d[WidthAxis], p.pos[WidthAxis], p.dim[WidthAxis]) > 0 &&
          overlap1d(z, d[DepthAxis], p.pos[DepthAxis], p.dim[DepthAxis]) > 0) {
        return false;
      }
    }
  }
  return true;
}

function collides(placed, d, x, y, z) {
  for (const p of placed) {
    if (overlap1d(x, d[WidthAxis], p.pos[WidthAxis], p.dim[WidthAxis]) > EPS &&
        overlap1d(y, d[HeightAxis], p.pos[HeightAxis], p.dim[HeightAxis]) > EPS &&
        overlap1d(z, d[DepthAxis], p.pos[DepthAxis], p.dim[DepthAxis]) > EPS) return true;
  }
  return false;
}

/**
 * items 를 (W,H,D: factored 정수) 빈에 EMS 방식으로 채운다.
 * @returns {{ placedNames: Set<string>, positions: Map<string,{position:number[],rotationType:number}>, count: number }}
 */
export function packEMS(W, H, D, items) {
  let minDim = Infinity;
  for (const it of items) {
    minDim = Math.min(minDim, it.getWidth(), it.getHeight(), it.getDepth());
  }
  if (!Number.isFinite(minDim)) minDim = 0;

  // 큰 상자부터 (First-Fit Decreasing)
  const order = items.slice().sort((a, b) => b.getVolume() - a.getVolume());

  let spaces = [mkSpace(0, 0, 0, W, H, D)];
  const placed = []; // { pos:[x,y,z], dim:[w,h,d], name }
  const positions = new Map();
  const placedNames = new Set();

  for (const item of order) {
    let best = null; // { score, x, y, z, dim, rot }

    for (const S of spaces) {
      for (const rot of item.allowedRotation) {
        item.rotationType = rot;
        const dim = item.getDimension();
        if (dim[WidthAxis] > sw(S) + EPS || dim[HeightAxis] > sh(S) + EPS || dim[DepthAxis] > sd(S) + EPS) continue;

        const x = S.x0, y = S.y0, z = S.z0;
        if (x + dim[WidthAxis] > W + EPS || z + dim[DepthAxis] > D + EPS || y + dim[HeightAxis] > H + EPS) continue;
        if (collides(placed, dim, x, y, z)) continue;
        if (isFullyFloating(placed, dim, x, y, z)) continue;

        // margin(작을수록 스너그) → y(낮게) → z → x
        const margin = (sw(S) - dim[WidthAxis]) + (sh(S) - dim[HeightAxis]) + (sd(S) - dim[DepthAxis]);
        const score = [margin, y, z, x];
        if (best === null || lexLess(score, best.score)) {
          best = { score, x, y, z, dim: [dim[WidthAxis], dim[HeightAxis], dim[DepthAxis]], rot };
        }
      }
    }

    if (!best) continue; // 이 빈엔 안 들어감 → 미배치

    placed.push({ pos: [best.x, best.y, best.z], dim: best.dim, name: item.name });
    placedNames.add(item.name);
    positions.set(item.name, { position: [best.x, best.y, best.z], rotationType: best.rot });

    const B = mkSpace(best.x, best.y, best.z, best.x + best.dim[0], best.y + best.dim[1], best.z + best.dim[2]);
    const next = [];
    for (const S of spaces) {
      if (overlaps(S, B)) next.push(...difference(S, B));
      else next.push(S);
    }
    spaces = prune(next, minDim);
  }

  return { placedNames, positions, count: placedNames.size };
}

function lexLess(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] < b[i]) return true;
    if (a[i] > b[i]) return false;
  }
  return false;
}
